import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const { refresh, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("Missing verification token");
      return;
    }
    api.post("/auth/verify-email/confirm", { token })
      .then(async () => {
        setStatus("ok");
        await refresh();
      })
      .catch((e) => {
        setStatus("error");
        setError(e.response?.data?.detail || "Verification failed");
      });
  }, [token, refresh]);

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <Card className="w-full max-w-md rounded-3xl border-emerald-100 p-10 text-center" data-testid="verify-email-card">
        {status === "loading" && (
          <>
            <Loader2 className="w-10 h-10 text-emerald-500 mx-auto mb-4 animate-spin" />
            <h1 className="font-display text-xl font-bold text-slate-900">Verifying your email…</h1>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Email verified!</h1>
            <p className="text-sm text-slate-500 mt-2">You&apos;re all set.</p>
            <Button onClick={() => navigate(user ? "/dashboard" : "/login")} className="mt-6 rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="verify-continue">
              Continue
            </Button>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Verification failed</h1>
            <p className="text-sm text-slate-500 mt-2">{error}</p>
            <Link to="/login" className="inline-block mt-6">
              <Button variant="outline" className="rounded-full">Back to sign in</Button>
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
